import { createMixin, get, map } from '@spaceship-fspl/substance-style';

var createColorFactory = function createColorFactory(properties) {
  return function (_ref) {
    var map = _ref.map,
        getColor = _ref.getColor;
    return createMixin({
      map: map,
      properties: properties,
      transform: getColor
    });
  };
};

var createColor = createColorFactory(['color']);
var createBackgroundColor = createColorFactory(['background-color']);
var createBorderColor = createColorFactory(['border-color']);
var createBorderTopColor = createColorFactory(['border-top-color']);
var createBorderRightColor = createColorFactory(['border-right-color']);
var createBorderBottomColor = createColorFactory(['border-bottom-color']);
var createBorderLeftColor = createColorFactory(['border-left-color']);
var createFill = createColorFactory(['fill']);

function createGetColor(colorsOrGetColors) {
  // TODO: warn about unknown colors
  if (typeof colorsOrGetColors === 'function') {
    return function (value) {
      return function (_ref) {
        var theme = _ref.theme;
        return get(value, colorsOrGetColors(theme)) || value;
      };
    };
  } else {
    return function (value) {
      return get(value, colorsOrGetColors) || value;
    };
  }
}

var getColors = function getColors(theme) {
  var _ref;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  var color = (_ref = theme) === null || _ref === void 0 ? void 0 : _ref.color;

  if (color) {
    return color;
  }

  return {};
};

var getColor = createGetColor(getColors);
var color = createColor({
  map: map,
  getColor: getColor
});
var backgroundColor = createBackgroundColor({
  map: map,
  getColor: getColor
});
var borderColor = createBorderColor({
  map: map,
  getColor: getColor
});
var borderTopColor = createBorderTopColor({
  map: map,
  getColor: getColor
});
var borderRightColor = createBorderRightColor({
  map: map,
  getColor: getColor
});
var borderBottomColor = createBorderBottomColor({
  map: map,
  getColor: getColor
});
var borderLeftColor = createBorderLeftColor({
  map: map,
  getColor: getColor
});
var fill = createFill({
  map: map,
  getColor: getColor
});

var createFactoryForCSSPropertyMixin = function createFactoryForCSSPropertyMixin(property) {
  var transform = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : function (value) {
    return value;
  };
  return function (_ref) {
    var map = _ref.map;
    return createMixin({
      map: map,
      properties: [property],
      transform: transform
    });
  };
};

var createFlex = createFactoryForCSSPropertyMixin('flex');
var createFlexBasis = createFactoryForCSSPropertyMixin('flex-basis');
var createFlexDirection = createFactoryForCSSPropertyMixin('flex-direction');
var createFlexGrow = createFactoryForCSSPropertyMixin('flex-grow');
var createFlexShrink = createFactoryForCSSPropertyMixin('flex-shrink');
var createAlignItems = createFactoryForCSSPropertyMixin('align-items');
var createJustifyContent = createFactoryForCSSPropertyMixin('justify-content');
var createFlexRowGap = createFactoryForCSSPropertyMixin('row-gap');
var createFlexColGap = createFactoryForCSSPropertyMixin('column-gap');
var flex = createFlex({
  map: map
});
var flexBasis = createFlexBasis({
  map: map
});
var flexDirection = createFlexDirection({
  map: map
});
var flexGrow = createFlexBasis({
  map: map
});
var rowGap = createFlexRowGap({
  map: map
});
var colGap = createFlexColGap({
  map: map
});
var flexShrink = createFlexBasis({
  map: map
});
var alignItems = createAlignItems({
  map: map
});
var justifyContent = createJustifyContent({
  map: map
});

var defaultSpaces = {
  0: 0,
  1: '0.25em',
  2: '0.5em',
  3: '1em',
  4: '2em',
  5: '4em'
};

function createGetSpace(spacesOrGetSpaces) {
  // TODO: warn about unknown spaces
  // TODO: support negative spacings
  if (typeof spacesOrGetSpaces === 'function') {
    return function (value) {
      return function (_ref) {
        var theme = _ref.theme;
        return get(String(value), spacesOrGetSpaces(theme)) || value;
      };
    };
  } else {
    return function (value) {
      return get(String(value), spacesOrGetSpaces) || value;
    };
  }
}

var createSpaceFactory = function createSpaceFactory(properties) {
  return function (_ref) {
    var map = _ref.map,
        getSpace = _ref.getSpace;
    return createMixin({
      map: map,
      properties: properties,
      transform: getSpace
    });
  };
};

var createMargin = createSpaceFactory(['margin-top', 'margin-right', 'margin-bottom', 'margin-left']);
var createMarginX = createSpaceFactory(['margin-right', 'margin-left']);
var createMarginY = createSpaceFactory(['margin-top', 'margin-bottom']);
var createMarginTop = createSpaceFactory(['margin-top']);
var createMarginRight = createSpaceFactory(['margin-right']);
var createMarginBottom = createSpaceFactory(['margin-bottom']);
var createMarginLeft = createSpaceFactory(['margin-left']);
var createPadding = createSpaceFactory(['padding-top', 'padding-right', 'padding-bottom', 'padding-left']);
var createPaddingX = createSpaceFactory(['padding-right', 'padding-left']);
var createPaddingY = createSpaceFactory(['padding-top', 'padding-bottom']);
var createPaddingTop = createSpaceFactory(['padding-top']);
var createPaddingRight = createSpaceFactory(['padding-right']);
var createPaddingBottom = createSpaceFactory(['padding-bottom']);
var createPaddingLeft = createSpaceFactory(['padding-left']);

var getSpaces = function getSpaces(theme) {
  var _ref;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  var space = (_ref = theme) === null || _ref === void 0 ? void 0 : _ref.space;

  if (space) {
    return space;
  }

  return defaultSpaces;
};

var getSpace = createGetSpace(getSpaces);
var margin = createMargin({
  map: map,
  getSpace: getSpace
});
var marginX = createMarginX({
  map: map,
  getSpace: getSpace
});
var marginY = createMarginY({
  map: map,
  getSpace: getSpace
});
var marginTop = createMarginTop({
  map: map,
  getSpace: getSpace
});
var marginRight = createMarginRight({
  map: map,
  getSpace: getSpace
});
var marginBottom = createMarginBottom({
  map: map,
  getSpace: getSpace
});
var marginLeft = createMarginLeft({
  map: map,
  getSpace: getSpace
});
var padding = createPadding({
  map: map,
  getSpace: getSpace
});
var paddingX = createPaddingX({
  map: map,
  getSpace: getSpace
});
var paddingY = createPaddingY({
  map: map,
  getSpace: getSpace
});
var paddingTop = createPaddingTop({
  map: map,
  getSpace: getSpace
});
var paddingRight = createPaddingRight({
  map: map,
  getSpace: getSpace
});
var paddingBottom = createPaddingBottom({
  map: map,
  getSpace: getSpace
});
var paddingLeft = createPaddingLeft({
  map: map,
  getSpace: getSpace
});

var createTextAlign = createFactoryForCSSPropertyMixin('text-align');
var createTextDecoration = createFactoryForCSSPropertyMixin('text-decoration');
var createFontFamily = createFactoryForCSSPropertyMixin('font-family');
var createFontSize = createFactoryForCSSPropertyMixin('font-size');
var createFontStyle = createFactoryForCSSPropertyMixin('font-style');
var createFontWeight = createFactoryForCSSPropertyMixin('font-weight');
var createLineHeight = createFactoryForCSSPropertyMixin('line-height');
var createLetterSpacing = createFactoryForCSSPropertyMixin('letter-spacing');
var textAlign = createTextAlign({
  map: map
});
var textDecoration = createTextDecoration({
  map: map
});
var fontFamily = createFontFamily({
  map: map
});
var fontSize = createFontSize({
  map: map
});
var fontStyle = createFontStyle({
  map: map
});
var fontWeight = createFontWeight({
  map: map
});
var letterSpacing = createLetterSpacing({
  map: map
});
var lineHeight = createLineHeight({
  map: map
});

var convertNumberToPx = function convertNumberToPx(value) {
  if (typeof value === 'number') {
    return "".concat(value, "px");
  }

  return value;
};

var createDisplay = createFactoryForCSSPropertyMixin('display');
var createWidth = createFactoryForCSSPropertyMixin('width', convertNumberToPx);
var createMaxWidth = createFactoryForCSSPropertyMixin('max-width', convertNumberToPx);
var createMinWidth = createFactoryForCSSPropertyMixin('min-width', convertNumberToPx);
var createHeight = createFactoryForCSSPropertyMixin('height', convertNumberToPx);
var createMaxHeight = createFactoryForCSSPropertyMixin('max-height', convertNumberToPx);
var createMinHeight = createFactoryForCSSPropertyMixin('min-height', convertNumberToPx);
var display = createDisplay({
  map: map
});
var width = createWidth({
  map: map
});
var maxWidth = createMaxWidth({
  map: map
});
var minWidth = createMinWidth({
  map: map
});
var height = createHeight({
  map: map
});
var maxHeight = createMaxHeight({
  map: map
});
var minHeight = createMinHeight({
  map: map
});
var createOpacity = createFactoryForCSSPropertyMixin('opacity');
var opacity = createOpacity({
  map: map
});
var createBorderRadius = createFactoryForCSSPropertyMixin('border-radius');
var borderRadius = createBorderRadius({
  map: map
});

export { alignItems, backgroundColor, borderBottomColor, borderColor, borderLeftColor, borderRadius, borderRightColor, borderTopColor, colGap, color, createAlignItems, createBackgroundColor, createBorderBottomColor, createBorderColor, createBorderLeftColor, createBorderRadius, createBorderRightColor, createBorderTopColor, createColor, createDisplay, createFill, createFlex, createFlexBasis, createFlexColGap, createFlexDirection, createFlexGrow, createFlexRowGap, createFlexShrink, createFontFamily, createFontSize, createFontStyle, createFontWeight, createGetColor, createGetSpace, createHeight, createJustifyContent, createLetterSpacing, createLineHeight, createMargin, createMarginBottom, createMarginLeft, createMarginRight, createMarginTop, createMarginX, createMarginY, createMaxHeight, createMaxWidth, createMinHeight, createMinWidth, createOpacity, createPadding, createPaddingBottom, createPaddingLeft, createPaddingRight, createPaddingTop, createPaddingX, createPaddingY, createTextAlign, createTextDecoration, createWidth, defaultSpaces, display, fill, flex, flexBasis, flexDirection, flexGrow, flexShrink, fontFamily, fontSize, fontStyle, fontWeight, getSpace, height, justifyContent, letterSpacing, lineHeight, margin, marginBottom, marginLeft, marginRight, marginTop, marginX, marginY, maxHeight, maxWidth, minHeight, minWidth, opacity, padding, paddingBottom, paddingLeft, paddingRight, paddingTop, paddingX, paddingY, rowGap, textAlign, textDecoration, width };
//# sourceMappingURL=mixin.esm.js.map
